import { message } from "antd";
import { type ILineItemDefaults, LineItemType } from "legacyComponents/LineItemContainer.types";
import { type NavigateFunction } from "react-router-dom-v5-compat";

import type { ParsedDocumentSelectionOption } from "@btnet/services-btnet/handlers/models/parsedDocumentSelectionOption";
import type { ParsedUrlSelectionOptionFields } from "@btnet/services-btnet/handlers/models/parsedUrlSelectionOptionFields";
import type { SelectionOptionCreationRequest } from "@btnet/services-btnet/handlers/models/selectionOptionCreationRequest";
import { getSelectionOptionsBySelectionOptionIdGetV2QueryKey } from "@btnet/services-btnet/handlers/selection-options/selection-options";
import { type IWebviewRouteParams } from "@btnet/utilities-webview";

import { queryClient } from "helpers/queryClient";

import { showApixErrorMessage } from "utilities/api/apix-handler";
import { newLineItemDefaults } from "utilities/lineItem/lineItemHelper";
import { recalculateAllFields } from "utilities/lineItem/markupUtility";
import { routes } from "utilities/routes";
import { shouldDefaultToTaxable } from "utilities/tax/tax.utilities";

import {
    btConfirmAsync,
    type ModalReturnTypes,
} from "commonComponents/btWrappers/BTConfirm/BTConfirm";

import { NewEntityId } from "entity/estimate/common/estimate.common.types";
import {
    SelectionOptionLineItem,
    transformSelectionOptionLineItemsToLineItems,
} from "entity/selectionChoice/SelectionChoice/SelectionChoice.api.types";
import { type ISelectionOptionFormValues } from "entity/selectionOptions/SelectionOption/SelectionOptionPresentational";
import { type JobTaxVersion } from "entity/tax/common/tax.types";
import { NoTaxId } from "entity/tax/TaxRate/TaxRate.api.types";

export type SelectionOptionAction = "approve" | "decline" | "reset" | "send";
export type StatusChangeAction = "approve" | "decline";

/**
 * Handles success/error messages for selection option approve/decline/reset actions
 * @param action - The action performed ("approve", "decline", "reset", or "send")
 * @param selectionOptionTitle - The title of the selection option
 * @param failedSelectionOptions - Object containing failed selection option IDs and their error messages
 */
export function handleSelectionOptionActionMessages(
    action: SelectionOptionAction,
    selectionOptionTitle: string,
    successCount: number,
    failedCount: number,
    selectionOptionIds: number[],
): void {
    // Convert action to past tense for display
    let actionPastTense: string;
    if (action === "approve") {
        actionPastTense = "approved";
    } else if (action === "decline") {
        actionPastTense = "declined";
    } else if (action === "send") {
        actionPastTense = "sent";
    } else {
        actionPastTense = "recalled";
    }

    if (failedCount === 0) {
        const count = selectionOptionIds.length;
        const successMessage =
            count === 1
                ? `${selectionOptionTitle} is ${actionPastTense}`
                : `${count} options ${actionPastTense}`;
        message.success(successMessage);
    } else if (successCount === 0) {
        // All failed
        showApixErrorMessage(
            new Error(`Selection option could not be ${actionPastTense}`),
            `Selection option could not be ${actionPastTense}`,
        );
    } else {
        // Partial success
        showApixErrorMessage(
            new Error(`Some selection options could not be ${actionPastTense}`),
            `${failedCount} of ${selectionOptionIds.length} selection options could not be ${actionPastTense}`,
        );
    }
}

/**
 * Shows a confirmation dialog for approve/decline actions
 * @param action - The action to perform ("approve" or "decline")
 * @param inSelectionOptionGroup - Whether the selection option is in a group
 * @param count - Number of selection options (defaults to 1 for singular messaging)
 * @returns Promise that resolves to true if confirmed, false if cancelled
 */
export async function showStatusChangeConfirmation(
    action: "approve" | "decline",
    inSelectionOptionGroup: boolean,
    count: number = 1,
): Promise<ModalReturnTypes> {
    const isApprove = action === "approve";

    const getConfirmationModalText = () => {
        const baseMessage = `${isApprove ? "Approving" : "Declining"} ${
            count > 1 ? "these options" : "this option"
        } `;
        if (inSelectionOptionGroup) {
            return `${baseMessage}will impact your allowance remaining amount`;
        } else {
            return `${baseMessage}will impact your budget and total client price`;
        }
    };

    const getModalTitle = () => {
        const actionText = isApprove ? "Approve" : "Decline";
        if (count === 1) {
            return `${actionText} option`;
        }
        return `${actionText} ${count} options`;
    };

    const userChoice = await btConfirmAsync({
        cancelText: "Cancel",
        content: getConfirmationModalText(),
        okText: isApprove ? "Approve" : "Decline",
        okType: isApprove ? "primary" : "danger",
        title: getModalTitle(),
        okButtonProps: {
            "data-testid": "confirmButton",
        },
        cancelButtonProps: { "data-testid": "cancelButton" },
    });

    return userChoice;
}

/**
 * Builds a SelectionOptionCreationRequest from form values
 * @param values - Form values from the selection option form
 * @param jobId - The job ID associated with the selection option
 * @param status - Optional status to set on the selection option (used for approve/decline)
 */
export function buildSelectionOptionRequest(
    values: ISelectionOptionFormValues,
    jobId: number,
): SelectionOptionCreationRequest {
    const lineItems = values.selectionOptionLineItems
        ? transformSelectionOptionLineItemsToLineItems(values)
        : [];
    return {
        description: values.description ?? "",
        jobId,
        title: values.title ?? "",
        lineItems,
        hasDeletedLineItems: values.hasDeletedLineItems,
        selectionOptionGroupId: values.selectionOptionGroupId ?? null,
        categoryId: values.categoryId ?? null,
        locationId: values.locationId ?? null,
        attachedFiles: {
            attachDocs: values.attachedFiles?.attachDocs,
        },
        productUrl: values.productUrl,
        imageUrl: values.imageUrl ?? null,
        deadlineDate: values.deadlineDate ?? null,
        wasAutofillUsed: values.wasAutofillUsed ?? false,
    };
}

function getDefaultCostCodeId(lineItemDefaults: ILineItemDefaults | undefined): number | null {
    // PopulateLineItemDefaults surfaces the builder's organization-wide default cost code via
    // defaults.costCode.value. Same pattern used by SubBill.api.types.ts when seeding a row.
    const defaultId = lineItemDefaults?.costCode?.value as number | undefined;
    return defaultId && defaultId > 0 ? defaultId : null;
}

export function getAllowanceCostCodeId(
    selectionOptionGroupId: number | null | undefined,
    costCodeIdsByGroupId: { [key: string]: number } | undefined,
): number | null {
    if (!selectionOptionGroupId || !costCodeIdsByGroupId) {
        return null;
    }
    const costCodeId = costCodeIdsByGroupId[String(selectionOptionGroupId)];
    return costCodeId && costCodeId > 0 ? costCodeId : null;
}

function buildSelectionOptionLineItem(
    id: number,
    itemTitle: string,
    description: string,
    unit: string,
    unitCost: number,
    defaultCostCodeId: number | null,
    taxId: number | undefined,
    isInEdit: boolean,
): SelectionOptionLineItem {
    const base = newLineItemDefaults(
        id,
        LineItemType.SelectionOption,
        null,
        defaultCostCodeId,
        isInEdit,
    );
    const item = new SelectionOptionLineItem({
        ...base,
        itemTitle,
        description,
        unit,
        unitCost,
        taxGroupId: taxId,
    });
    return new SelectionOptionLineItem({ ...item, ...recalculateAllFields(item) });
}

/**
 * Applies the AI-parsed product fields to the selection option form.
 */
export function applyParseUrlResultToForm(
    fields: ParsedUrlSelectionOptionFields,
    setFieldValue: (field: string, value: unknown) => void,
    lineItemDefaults: ILineItemDefaults | undefined,
    taxGroupId: number | undefined,
    effectiveTaxVersion: JobTaxVersion | undefined,
    predictedCostCodeId?: number | null,
    allowanceCostCodeId?: number | null,
): void {
    if (fields.title != null) {
        setFieldValue("title", fields.title);
    }
    if (fields.description != null) {
        setFieldValue("description", fields.description);
    }
    setFieldValue("imageUrl", fields.imageUrl ?? null);

    // The allowance's cost code wins over the AI prediction (and the builder default), so options
    // created under an allowance still roll up to that allowance's cost code; still just the
    // initial value, so the user can change or clear it like any other line item cost code.
    const defaultCostCodeId =
        allowanceCostCodeId ?? predictedCostCodeId ?? getDefaultCostCodeId(lineItemDefaults);
    const taxId = shouldDefaultToTaxable(taxGroupId, effectiveTaxVersion) ? taxGroupId : NoTaxId;
    const lineItem = buildSelectionOptionLineItem(
        NewEntityId,
        fields.title ?? "",
        fields.description ?? "",
        fields.unit ?? "",
        fields.price ?? 0,
        defaultCostCodeId,
        taxId,
        true,
    );

    setFieldValue("selectionOptionLineItems", [lineItem]);
    setFieldValue("wasAutofillUsed", true);
}

/**
 * Applies AI-parsed document fields to the selection option form. Unlike the URL-parse version,
 * this maps the full lineItems array — each parsed line item becomes a separate row. Title and
 * description are set only when the parser returned a value. When the document contained no line
 * items a single default row is created using the option title so the form is never left empty.
 */
export function applyDocumentParseResultToForm(
    option: ParsedDocumentSelectionOption,
    setFieldValue: (field: string, value: unknown) => void,
    lineItemDefaults: ILineItemDefaults | undefined,
    taxGroupId: number | undefined,
    effectiveTaxVersion: JobTaxVersion | undefined,
    allowanceCostCodeId?: number | null,
): void {
    if (option.title != null) setFieldValue("title", option.title);
    if (option.description != null) setFieldValue("description", option.description);

    const defaultCostCodeId = allowanceCostCodeId ?? getDefaultCostCodeId(lineItemDefaults);
    const taxId = shouldDefaultToTaxable(taxGroupId, effectiveTaxVersion) ? taxGroupId : NoTaxId;
    const build = (index: number, title: string, desc: string, unit: string, price: number) =>
        buildSelectionOptionLineItem(
            NewEntityId - index,
            title,
            desc,
            unit,
            price,
            defaultCostCodeId,
            taxId,
            false,
        );

    const lineItems = option.lineItems?.length
        ? option.lineItems.map((li, index) =>
              build(index, li.title ?? "", li.description ?? "", li.unit ?? "", li.price ?? 0),
          )
        : [build(0, option.title ?? "", option.description ?? "", "", 0)];

    setFieldValue("selectionOptionLineItems", lineItems);
    setFieldValue("wasAutofillUsed", true);
}

/**
 * Invalidates query cache and navigates to view mode after a status change
 */
export async function refreshAndNavigateToViewMode(
    selectionOptionId: number | undefined,
    jobId: number,
    navigate: NavigateFunction,
    setIsManuallyEditing: (value: boolean) => void,
    currentState?: string,
    isModal?: boolean,
    webviewRouteParams?: IWebviewRouteParams,
): Promise<void> {
    if (!selectionOptionId) return;

    // Invalidate query cache to refetch with updated status
    const queryKey = getSelectionOptionsBySelectionOptionIdGetV2QueryKey(selectionOptionId);
    await queryClient.invalidateQueries({ queryKey });

    // Exit edit mode state before navigating
    setIsManuallyEditing(false);

    // In modal context, skip URL navigation — state change and cache invalidation
    // are sufficient, and absolute route navigation would break out of the modal.
    if (isModal) return;

    // Only navigate if we're not already in view mode
    if (currentState !== "View") {
        // In webview context, navigate to the webview detail URL so we don't drop
        // out of the webview shell and bounce through the SPA list redirect.
        const viewLink = webviewRouteParams
            ? routes.selectionOptions.getWebviewDetailsLink(
                  selectionOptionId,
                  jobId,
                  webviewRouteParams,
                  "View",
              )
            : routes.selectionOptions.getDetailsLink(selectionOptionId, jobId, "View");
        navigate(viewLink, { replace: true });
    }
}
